import type { UserData, DoctorProfile, TeamData } from './types'

interface OnboardingChecklistProps {
  userData: UserData
  doctors: DoctorProfile[]
  teamData: TeamData | null
  selectedDoctor: DoctorProfile | null
  onDismiss: () => void
  onAddDoctor: () => void
  onEditDoctor: () => void
  onOpenAppeals: () => void
  onOpenTeam: () => void
}

export default function OnboardingChecklist({
  userData,
  doctors,
  teamData,
  selectedDoctor,
  onDismiss,
  onAddDoctor,
  onEditDoctor,
  onOpenAppeals,
  onOpenTeam,
}: OnboardingChecklistProps) {
  const accountCreatedAt = userData.createdAt ? new Date(userData.createdAt) : null
  const daysSinceSignup = accountCreatedAt ? (Date.now() - accountCreatedAt.getTime()) / (1000 * 60 * 60 * 24) : 999

  if (!userData.loggedIn || daysSinceSignup >= 30) return null

  const hasLocation = doctors.some(d => d.locations && d.locations.length > 0 && d.locations.some(l => l.address.trim() !== ''))
  const steps = [
    { id: 'account', label: 'Create your account', done: true, action: null as (() => void) | null, bonus: false },
    { id: 'doctor', label: 'Add your first doctor profile', done: doctors.length > 0, action: onAddDoctor, bonus: false },
    { id: 'location', label: 'Add a practice location', done: hasLocation, action: () => { if (selectedDoctor) { onEditDoctor() } else { onAddDoctor() } }, bonus: false },
    { id: 'letter', label: 'Generate your first letter', done: (userData.lmnCount || 0) > 0, action: null as (() => void) | null, bonus: false },
    { id: 'appeal', label: 'Track your first appeal outcome', done: false, action: onOpenAppeals, bonus: false },
    { id: 'team', label: 'Invite a team member', done: (teamData?.members?.length || 0) > 0, action: onOpenTeam, bonus: true },
  ]
  const requiredSteps = steps.filter(s => !s.bonus)
  const completedRequired = requiredSteps.filter(s => s.done).length
  const totalRequired = requiredSteps.length
  const completedAll = steps.filter(s => s.done).length
  const totalAll = steps.length
  const allRequiredDone = completedRequired === totalRequired

  return (
    <div className="max-w-[1600px] mx-auto px-6 pt-6">
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white flex items-center justify-center text-lg font-bold shadow-sm">
              {allRequiredDone ? '\u{1F389}' : '\u{1F4CB}'}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">
                {allRequiredDone ? 'Congratulations! Setup Complete!' : 'Welcome to RxCheckUp\u2122!'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {allRequiredDone
                  ? "You're all set — run a PA check or generate your first letter."
                  : 'Complete these steps to get the most out of your account.'}
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors text-sm flex items-center gap-1"
            title="Dismiss checklist"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Dismiss
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-teal-700">{completedAll} of {totalAll} complete</span>
            {allRequiredDone && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">All essentials done!</span>}
          </div>
          <div className="w-full h-2 bg-white/80 rounded-full overflow-hidden border border-teal-100">
            <div
              className={`h-full rounded-full transition-all duration-500 ${allRequiredDone ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : 'bg-gradient-to-r from-teal-400 to-cyan-400'}`}
              style={{ width: `${(completedAll / totalAll) * 100}%` }}
            />
          </div>
        </div>

        {/* Confetti decoration when all required are done */}
        {allRequiredDone && (
          <div className="px-6 pb-2">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
              <span className="text-2xl">{'\u{1F38A}'}</span>
              <div>
                <p className="text-sm font-bold text-emerald-800">You're all set up!</p>
                <p className="text-xs text-emerald-600">Your account is fully configured. Keep using RxCheckUp to generate winning appeal letters.</p>
              </div>
              <span className="text-2xl">{'\u2728'}</span>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="px-6 pb-5 space-y-1">
          {steps.map((step) => (
            <div key={step.id} className={`flex items-center gap-3 py-2 px-3 rounded-lg transition-colors ${step.done ? 'bg-white/40' : 'bg-white/70 hover:bg-white/90'}`}>
              {step.done ? (
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-slate-300 flex-shrink-0" />
              )}
              <span className={`text-sm flex-1 ${step.done ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>
                {step.label}
                {step.bonus && <span className="ml-1.5 text-[10px] font-bold text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded-full">BONUS</span>}
              </span>
              {!step.done && step.action && (
                <button
                  onClick={step.action}
                  className="text-teal-600 hover:text-teal-800 text-sm font-semibold flex items-center gap-1 transition-colors flex-shrink-0"
                >
                  <span className="text-xs">Go</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
